import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, IconButton, Stack, alpha } from '@mui/material';
import { GridColDef, GridColumnVisibilityModel, GridSortModel, getGridNumericOperators } from '@mui/x-data-grid-pro';
import { useNavigate } from 'react-router-dom';
import { FlagIcon } from 'src/components/iconify/flagIcon';
import SvgColor from 'src/components/svg-color';
import countriesJson from 'src/utils/countries.json';
import { formatCurrency } from 'src/utils/format-currency';
import { fShortenNumber, NumberFormatter } from 'src/utils/format-number';
import {
    renderMetricCell,
    renderBrandTrafficCell,
    compareColorPct,
    formatComparePct,
    BRAND_TRAFFIC_3WAY_COLORS,
} from 'src/components/data-grid/data-grid-performance-split-business-overview';
import CustomTooltip from '../components/CustomTooltip';
import Iconify from '../../iconify';
import CompetitionEvolutionCell from '../components/CompetitionEvolutionCell';
import HoverShortcut from '../components/HoverShortcut';
import { ResponsiveConfig } from './use-responsive-config';
import { domainFavicon } from '../utils';
import { PresenceSegments } from '../types';
import PresenceRateChartNew from '../components/PresenceRateChartNew';
import { useSelector } from 'react-redux';

// Amber of the Ad cost / Ad clicks bars in the mock-up. Local to this grid: these
// two columns are not driven by the KPI catalog, unlike the Explore metric bars
// that read `kpi.color`.
const AD_METRICS_BAR_COLOR = '#DFA52B';

// Bar color of the Visibility > Search terms Ad cost column: the KPI catalog's
// `ad_cost` row (api `traffic/metrics.csv`), which the Traffic page draws it in.
const SEARCH_TERMS_AD_COST_COLOR = '#DFAE4E';

type SearchTermsTrafficField = 'adCost' | 'brandTraffic' | 'paidTraffic' | 'organicTraffic';

/**
 * Default column visibility of the Search terms grid: Ad cost and Brand traffic are
 * shown, Paid / Organic traffic are hidden and can be shown again from the columns
 * panel.
 */
export const SEARCH_TERMS_COLUMN_VISIBILITY: GridColumnVisibilityModel = {
    paidTraffic: false,
    organicTraffic: false,
};

/**
 * Width of the value slot of a metric cell, sized on the column's widest value —
 * the page-max the bar scales to. Derived from `max` alone so it is identical on
 * every row and all the bars stay aligned. Mirrors the Explore metric cells.
 */
const valueSlotWidth = (formattedMax: string) => `${Math.max(7, formattedMax.length + 1)}ch`;

/**
 * Signed delta of a rate, in points — `+35pp`.
 *
 * Not the shared grid's `formatCompareDiff(..., 'percent')`: that one forces one
 * decimal (`+35.0pp`), whereas these rates are integers on the SQL side and the
 * mock-up shows them without a decimal.
 */
const formatCoverageDelta = (current: number, compare: number) => {
    const diff = Math.round(current) - Math.round(compare);
    return `${diff > 0 ? '+' : diff < 0 ? '-' : ''}${Math.abs(diff)}pp`;
};

/**
 * Width of the evolution slot laid out to the right of the bar, in comparison
 * mode. Same value as the shared grid's `renderMetricCell`, so the `+Xpp` / `+X%`
 * of every column line up vertically.
 */
const EVOLUTION_SLOT_WIDTH = 56;

/**
 * Visibility > Local column geometry, from the design review of the page.
 *
 * Only Local: the other Visibility grids size their columns from
 * `useResponsiveConfig`, and their own review has not run.
 */
const LOCAL_COMPETITORS_MIN_WIDTH = 240;
const LOCAL_VISIBILITY_RATE_WIDTH = 200;

const getPresenceTone = (rate?: number) => {
    if (typeof rate !== 'number') return 'warning';
    if (rate < 20) return 'danger';
    if (rate < 40) return 'warning';
    return 'success';
};

const numericFilterOperators = getGridNumericOperators().filter(
    (operator) => operator.value === '=' || operator.value === '!='
);

const TopCompetitorPillContent = ({
    domain,
    rate,
    iconSize,
    fontSize,
    onClick,
}: {
    domain: string;
    rate?: number;
    iconSize: number;
    fontSize: number;
    onClick?: (event: React.MouseEvent) => void;
}) => {
    const tone = getPresenceTone(rate);

    return (
        <Box
            onClick={onClick}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                pl: 1,
                pr: 1,
                py: 0.25,
                borderRadius: '32px',
                backgroundColor: 'rgba(145, 158, 171, 0.1)',
                cursor: onClick ? 'pointer' : 'default',
                transition: 'background-color 0.15s',
                '&:hover': onClick ? {
                    backgroundColor: 'rgba(0, 108, 156, 0.08)',
                    '& .pill-domain-label': {
                        color: '#006C9C',
                        textDecoration: 'underline',
                    },
                } : undefined,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5, gap: 0.5 }}>
                <Box
                    component="img"
                    src={domainFavicon(domain)}
                    sx={{
                        width: iconSize,
                        height: iconSize,
                        borderRadius: '12px',
                        backgroundColor: '#fff',
                        display: 'block',
                    }}
                />
                <Typography
                    className="pill-domain-label"
                    variant="body2"
                    sx={{
                        fontSize,
                        lineHeight: '22px',
                        color: '#333C47',
                        maxWidth: 180,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        transition: 'color 0.15s',
                    }}
                >
                    {domain}
                </Typography>
            </Box>
        </Box>
    );
};

const TopCompetitorPill = ({
    domain,
    rate,
    iconSize,
    fontSize,
    countryName,
}: {
    domain: string;
    rate?: number;
    iconSize: number;
    fontSize: number;
    countryName?: string;
}) => {
    const navigate = useNavigate();

    const handleClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        const countryEntry = countriesJson?.find((c: any) => c.name === countryName);
        const code = countryEntry?.code?.toUpperCase() || '';
        navigate(`/brand/visibility/ads?country=${encodeURIComponent(code)}&domain=${encodeURIComponent(domain)}`);
    };

    return (
        <TopCompetitorPillContent
            domain={domain}
            rate={rate}
            iconSize={iconSize}
            fontSize={fontSize}
            onClick={handleClick}
        />
    );
};

const getVisibleCount = (widths: number[], gap: number, availableWidth: number) => {
    let used = 0;
    let count = 0;
    widths.forEach((width) => {
        const next = count === 0 ? width : width + gap;
        if (used + next <= availableWidth + 0.5) {
            used += next;
            count += 1;
        }
    });
    return count;
};

const NewCompetitorsCell = ({
    competitors,
    responsiveConfig,
    countryName,
}: {
    competitors: {
        domainName: string;
        paidPresenceRate: number;
        rank?: number;
    }[];
    responsiveConfig: ResponsiveConfig;
    countryName?: string;
}) => {
    const navigate = useNavigate();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const measureRef = useRef<HTMLDivElement | null>(null);

    const [visibleCount, setVisibleCount] = useState(competitors.length);
    const [truncatedCount, setTruncatedCount] = useState(0);

    const pillIconSize = responsiveConfig.iconSize.domain + 5;
    const fontSize = responsiveConfig.fontSize.body;

    const competitorKey = useMemo(
        () => competitors.map((item) => item.domainName).join('|'),
        [competitors]
    );

    const handleCompetitorClick = (
        event: React.MouseEvent,
        domain: string
    ) => {
        event.stopPropagation();

        const countryEntry = countriesJson?.find(
            (country: any) => country.name === countryName
        );

        const code = countryEntry?.code?.toUpperCase() || '';

        navigate(
            `/brand/visibility/ads?country=${encodeURIComponent(
                code
            )}&domain=${encodeURIComponent(domain)}`
        );
    };

    useLayoutEffect(() => {
        const container = containerRef.current;
        const measure = measureRef.current;

        if (!container || !measure) return;

        const compute = () => {
            const children = Array.from(
                measure.children
            ) as HTMLElement[];

            const styles = window.getComputedStyle(measure);
            const gap =
                parseFloat(styles.columnGap || styles.gap || '0') || 0;

            const widths = children.map(
                (child) => child.getBoundingClientRect().width
            );

            const totalCount = widths.length;

            if (totalCount === 0) {
                setVisibleCount(0);
                setTruncatedCount(0);
                return;
            }

            const containerWidth =
                container.getBoundingClientRect().width;

            const fitWithoutOverlay = getVisibleCount(
                widths,
                gap,
                containerWidth
            );

            if (fitWithoutOverlay >= totalCount) {
                setVisibleCount(totalCount);
                setTruncatedCount(0);
                return;
            }

            const overlayWidth = 35;

            const fitWithOverlay = getVisibleCount(
                widths,
                gap,
                Math.max(0, containerWidth - overlayWidth)
            );

            const allowOverflowCount = Math.min(
                totalCount,
                fitWithOverlay + 1
            );

            setVisibleCount(Math.max(1, allowOverflowCount));
            setTruncatedCount(
                Math.max(0, totalCount - fitWithOverlay)
            );
        };

        compute();

        const observer = new ResizeObserver(compute);
        observer.observe(container);

        return () => observer.disconnect();
    }, [competitorKey, pillIconSize, fontSize]);

    const visibleCompetitors = competitors.slice(0, visibleCount);
    const remainingCount = Math.max(0, truncatedCount);

    return (
        <Box
            ref={containerRef}
            sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                height: '100%',
                mr: -2,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    minWidth: 0,
                    overflow: 'hidden',
                }}
            >
                {visibleCompetitors.map((competitor) => (
                    <TopCompetitorPillContent
                        key={competitor.domainName}
                        domain={competitor.domainName}
                        rate={competitor.paidPresenceRate}
                        iconSize={pillIconSize}
                        fontSize={fontSize}
                        onClick={(event) =>
                            handleCompetitorClick(
                                event,
                                competitor.domainName
                            )
                        }
                    />
                ))}
            </Box>

            {remainingCount > 0 && (
                <Box
                    sx={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        scale: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 20,
                        backdropFilter: 'blur(1px)',
                        backgroundColor: 'rgba(240, 240, 240, 0.7)',
                        pointerEvents: 'none',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 7,
                            lineHeight: '22px',
                            color: '#2F3843',
                            fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        +{remainingCount}
                    </Typography>
                </Box>
            )}

            <Box
                ref={measureRef}
                sx={{
                    position: 'absolute',
                    visibility: 'hidden',
                    pointerEvents: 'none',
                    height: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                }}
            >
                {competitors.map((competitor) => (
                    <TopCompetitorPillContent
                        key={`measure-new-${competitor.domainName}`}
                        domain={competitor.domainName}
                        rate={competitor.paidPresenceRate}
                        iconSize={pillIconSize}
                        fontSize={fontSize}
                    />
                ))}
            </Box>
        </Box>
    );
};

const TopCompetitorsCell = ({
    competitors,
    rates,
    responsiveConfig,
    countryName,
    t,
}: {
    competitors: string[];
    rates: Record<string, number>;
    responsiveConfig: ResponsiveConfig;
    countryName?: string;
    t: (key: string) => string;
}) => {
    const navigate = useNavigate();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const measureRef = useRef<HTMLDivElement | null>(null);
    const [visibleCount, setVisibleCount] = useState(competitors.length);
    const [truncatedCount, setTruncatedCount] = useState(0);
    const [hovered, setHovered] = useState(false);

    const handleSeeEvolutionClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        const countryEntry = countriesJson?.find((c) => c.name === countryName);
        const code = countryEntry?.code?.toUpperCase() || '';
        navigate(`/brand/visibility/competitors?country=${encodeURIComponent(code)}`);
    };

    const pillIconSize = responsiveConfig.iconSize.domain + 5;
    const fontSize = responsiveConfig.fontSize.body;

    const competitorKey = useMemo(() => competitors.join('|'), [competitors]);

    useLayoutEffect(() => {
        const container = containerRef.current;
        const measure = measureRef.current;
        if (!container || !measure) return;

        const compute = () => {
            const children = Array.from(measure.children) as HTMLElement[];
            const styles = window.getComputedStyle(measure);
            const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
            const widths = children.map((child) => child.getBoundingClientRect().width);
            const totalCount = widths.length;
            if (totalCount === 0) {
                setVisibleCount(0);
                return;
            }

            const containerWidth = container.getBoundingClientRect().width;
            const fitWithoutOverlay = getVisibleCount(widths, gap, containerWidth);
            if (fitWithoutOverlay >= totalCount) {
                setVisibleCount(totalCount);
                setTruncatedCount(0);
                return;
            }

            const overlayWidth = 35;
            const fitWithOverlay = getVisibleCount(widths, gap, Math.max(0, containerWidth - overlayWidth));
            const allowOverflowCount = Math.min(totalCount, fitWithOverlay + 1);
            setVisibleCount(Math.max(1, allowOverflowCount));
            setTruncatedCount(Math.max(0, totalCount - fitWithOverlay));
        };

        compute();
        const observer = new ResizeObserver(compute);
        observer.observe(container);
        return () => observer.disconnect();
    }, [competitorKey, pillIconSize, fontSize]);

    const visibleCompetitors = competitors.slice(0, visibleCount);
    const remainingCount = Math.max(0, truncatedCount);

    return (
        <Box
            ref={containerRef}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                height: '100%',
                mr: -2,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    minWidth: 0,
                    overflow: 'hidden',
                    pr: 0,
                }}
            >
                {visibleCompetitors.map((competitor: string) => (
                    <TopCompetitorPill
                        key={competitor}
                        domain={competitor}
                        rate={rates[competitor]}
                        iconSize={pillIconSize}
                        fontSize={fontSize}
                        countryName={countryName}
                    />
                ))}
            </Box>
            {remainingCount > 0 ? (
                <Box
                    sx={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        scale: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 20,
                        p: 0,
                        backdropFilter: 'blur(1px)',
                        backgroundColor: 'rgba(240, 240, 240, 0.7)',
                        pointerEvents: 'none',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 7,
                            lineHeight: '22px',
                            color: '#2F3843',
                            fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        {`+${remainingCount}`}
                    </Typography>
                </Box>
            ) : null}
            <Box
                ref={measureRef}
                sx={{
                    position: 'absolute',
                    visibility: 'hidden',
                    pointerEvents: 'none',
                    height: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                }}
            >
                {competitors.map((competitor: string) => (
                    <TopCompetitorPillContent
                        key={`measure-${competitor}`}
                        domain={competitor}
                        rate={rates[competitor]}
                        iconSize={pillIconSize}
                        fontSize={fontSize}
                    />
                ))}
            </Box>
        </Box>
    );
};

const VisibilityRateCell = ({
    row,
    responsiveConfig,
    PresenceRateChart,
    selectedCountry,
    columnType,
    compare = false,
    t,
}: {
    row: any;
    responsiveConfig: ResponsiveConfig;
    PresenceRateChart: React.ComponentType<{
        data: PresenceSegments;
        height?: number;
        totalVisibilityRate?: number;
    }>;
    selectedCountry?: string | null;
    columnType?: 'country' | 'searchTerm' | 'location';
    compare?: boolean;
    t: (key: string) => string;
}) => {
    const navigate = useNavigate();
    const [hovered, setHovered] = useState(false);

    // Only Visibility > Local compares periods this way; elsewhere the row has no
    // such field and the cell renders exactly as before.
    const evolution = row?.visibilityRateEvolution;
    const hasEvolution = typeof evolution === 'number' && !isNaN(evolution);

    const countryName =
        columnType === 'country'
            ? row?.country
            : selectedCountry || row?.country;

    const value =
        typeof row?.blendedPresenceRate === 'number'
            ? row.blendedPresenceRate
            : typeof row?.blended_presence_rate === 'number'
                ? row.blended_presence_rate
                : 0;

    const compareData = row?.presenceRateDataCompare;

    const compareValue =
        compareData &&
            (
                typeof compareData.organicOnly === 'number' ||
                typeof compareData.soloBidder === 'number' ||
                typeof compareData.topAdPosition === 'number'
            )
            ? (
                (compareData.organicOnly ?? 0) +
                (compareData.soloBidder ?? 0) +
                (compareData.topAdPosition ?? 0)
            )
            : null;

    const differencePp =
        compare && compareValue !== null
            ? Math.round(value - compareValue)
            : null;

    const handleSeeAdsClick = (e: React.MouseEvent) => {
        e.stopPropagation();

        const countryEntry = countriesJson?.find(
            (country) => country.name === countryName
        );

        const code =
            countryEntry?.code?.toUpperCase() || '';

        const brandDomain = row?.brandDomain;

        const url =
            `/brand/visibility/ads?country=${encodeURIComponent(code)}` +
            (
                brandDomain
                    ? `&domain=${encodeURIComponent(brandDomain)}`
                    : ''
            );

        navigate(url);
    };

    const hoverLabelSeeAds = (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.25,
            }}
        >
            {t('dashboard.global.visibility.seeYourAds')}

            <Iconify
                icon="eva:diagonal-arrow-right-up-fill"
                width={14}
            />
        </Box>
    );

    return (
        <Box
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: responsiveConfig.spacing.padding,
                width: '100%',
            }}
        >
            <Typography
                variant="body2"
                sx={{
                    fontWeight: 500,
                    minWidth: responsiveConfig.iconSize.flag + 28,
                    maxWidth: responsiveConfig.iconSize.flag + 28,
                    textAlign: 'right',
                    fontSize: responsiveConfig.fontSize.body,
                }}
            >
                {Math.round(value)}%
            </Typography>

            <Box
                sx={{
                    position: 'relative',
                    flex: '1 1 0',
                    minWidth: 0,
                    width: 0,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                <PresenceRateChartNew
                    data={row?.presenceRateData}
                    height={12}
                    totalVisibilityRate={value}
                />

                {hovered && (
                    <Box
                        sx={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: 152,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            background:
                                'linear-gradient(90deg, rgba(249,249,250,0) 0%, rgba(249,249,250,.55) 25%, rgba(249,249,250,.9) 45%, #F9F9FA 70%, #F9F9FA 100%)',
                        }}
                    >
                        <HoverShortcut
                            defaultContent=""
                            hoverContent={hoverLabelSeeAds}
                            onClick={handleSeeAdsClick}
                            forceHovered
                            fontSize={responsiveConfig.fontSize.body}
                            sx={{ ml: 0 }}
                        />
                    </Box>
                )}
            </Box>

            {differencePp !== null && (
                <Typography
                    variant="body2"
                    sx={{
                        minWidth: 65,
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                        fontSize: responsiveConfig.fontSize.body,
                        fontWeight: 500,
                        color:
                            differencePp > 0
                                ? '#00A76F'
                                : differencePp < 0
                                    ? '#FF3030'
                                    : 'text.secondary',
                    }}
                >
                    {differencePp > 0 ? '+' : ''}
                    {differencePp}pp
                </Typography>
            )}

            {hasEvolution && (
                <Typography
                    aria-label="visibility-rate-evolution"
                    sx={{
                        width: EVOLUTION_SLOT_WIDTH,
                        flexShrink: 0,
                        textAlign: 'right',
                        fontSize: '12px',
                        lineHeight: 1.2,
                        whiteSpace: 'nowrap',
                        fontVariantNumeric: 'tabular-nums',
                        color: compareColorPct(evolution, 0),
                    }}
                >
                    {formatCoverageDelta(evolution, 0)}
                </Typography>
            )}
        </Box>
    );
};

const countryCodeToLanguage: Record<string, string> = {
    gb: 'en', us: 'en', ca: 'en', au: 'en', nz: 'en', ie: 'en',
    za: 'en', in: 'en', sg: 'en', ph: 'en', my: 'en',
    br: 'pt', at: 'de', ch: 'de', be: 'fr', mx: 'es',
    ar: 'es', cl: 'es', co: 'es', pe: 'es',
};

interface DataGridConfigProps {
    responsiveConfig: ResponsiveConfig;
    theme: any;
    t: (key: string) => string;
    workspace?: any;
    sortModel: GridSortModel;
    toggleTrafficShareSort: () => void;
    PresenceRateChart: React.ComponentType<{ data: PresenceSegments; height?: number; totalVisibilityRate?: number }>;
    columnType?: 'country' | 'searchTerm' | 'location';
    compare?: boolean;
    cellMode?: 'overview' | 'searchTermAnalysis';
    selectedCountry?: string | null;
    /**
     * Page-max of the Ad cost / Ad clicks columns, which their in-cell bars scale
     * to. Passed in rather than derived per cell: the bar is only comparable if
     * every row divides by the same maximum, and the cell renderer sees one row.
     */
    adMetricsMax?: { adCost?: number; adClicks?: number };
    /**
     * Visibility > Search terms only: passing it adds the Ad cost / Brand / Paid /
     * Organic traffic columns. The Ad cost bar scales to this page-max (same role as
     * `adMetricsMax`).
     */
    trafficMetricsMax?: { adCost?: number };
}

const SearchTermCell = ({
    row,
    responsiveConfig,
    cellMode = 'searchTermAnalysis',
    selectedCountry,
    t,
}: {
    row: any;
    responsiveConfig: ResponsiveConfig;
    cellMode?: 'overview' | 'searchTermAnalysis';
    selectedCountry?: string | null;
    t: (key: string) => string;
}) => {
    const navigate = useNavigate();
    const term = row?.searchTerm;
    const ts = typeof row?.trafficShare === 'number' ? `${Math.round(row.trafficShare)}%` : '-';

    const handleHoverClick = (e: React.MouseEvent) => {
        if (cellMode === 'overview') {
            const countryName = row?.country || selectedCountry;
            const countryEntry = countriesJson?.find((c) => c.name === countryName);
            const code = countryEntry?.code?.toUpperCase() || '';
            const stPath = `/brand/visibility/search-terms?country=${encodeURIComponent(code)}&q=${encodeURIComponent(term || '')}`;
            navigate(stPath);
        } else {
            const countryName = row?.country || selectedCountry;
            const countryEntry = countriesJson?.find((c) => c.name === countryName);
            const cc = countryEntry?.code?.toLowerCase() || '';
            const hl = countryCodeToLanguage[cc] || cc;
            window.open(
                `https://www.google.com/search?q=${encodeURIComponent(term || '')}&gl=${cc}&hl=${hl}`,
                '_blank',
            );
        }
    };

    const hoverLabel = (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
            {cellMode === 'overview' ? t('dashboard.global.visibility.seeST') : t('dashboard.global.visibility.search')}
            <Iconify icon="eva:diagonal-arrow-right-up-fill" width={14} />
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <CustomTooltip title={term ?? ''} placement="top">
                <Typography
                    variant="body2"
                    sx={{
                        fontSize: responsiveConfig.fontSize.body,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {term}
                </Typography>
            </CustomTooltip>
            <HoverShortcut
                defaultContent={ts}
                hoverContent={hoverLabel}
                onClick={handleHoverClick}
                fontSize={responsiveConfig.fontSize.body}
            />
        </Box>
    );
};

const CountryCell = ({
    row,
    responsiveConfig,
    t,
}: {
    row: any;
    responsiveConfig: ResponsiveConfig;
    t: (key: string) => string;
}) => {
    const navigate = useNavigate();
    const [hovered, setHovered] = useState(false);
    const countryName = row?.country;
    const country = countriesJson?.find((c) => c.name === countryName);
    const flagCode = country ? country.code : null;
    const ts = typeof row?.trafficShare === 'number' ? `${Math.round(row.trafficShare)}%` : '-';

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        const code = country?.code?.toUpperCase() || '';
        navigate(`/brand/visibility/search-terms?country=${encodeURIComponent(code)}`);
    };

    return (
        <Box
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: responsiveConfig.spacing.column, minWidth: 0 }}>
                {flagCode ? (
                    <FlagIcon
                        isCircle={false}
                        code={flagCode}
                        sx={{
                            width: 22.4,
                            height: 16,
                            aspectRatio: '7 / 5',
                            flexShrink: 0,
                        }}
                    />
                ) : (
                    <img
                        src="/assets/location.png"
                        alt="location"
                        style={{
                            width: 22.4,
                            height: 16,
                            aspectRatio: '7 / 5',
                            flexShrink: 0,
                            objectFit: 'contain',
                        }}
                    />
                )}
                <Typography variant="body2" sx={{ fontSize: responsiveConfig.fontSize.body, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {countryName}
                </Typography>
            </Box>
            {hovered ? (
                <Typography
                    variant="body2"
                    onClick={handleClick}
                    sx={{
                        fontWeight: 'medium',
                        fontSize: responsiveConfig.fontSize.body,
                        color: '#006C9C',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        ml: 1,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.25,
                        '&:hover': { textDecoration: 'underline' },
                    }}
                >
                    {t('dashboard.global.visibility.seeST')}
                    <Iconify icon="eva:diagonal-arrow-right-up-fill" width={14} />
                </Typography>
            ) : (
                <Typography variant="body2" color="rgba(99, 115, 129, 1)" sx={{ fontWeight: 'medium', fontSize: responsiveConfig.fontSize.body, whiteSpace: 'nowrap', ml: 1 }}>
                    {ts}
                </Typography>
            )}
        </Box>
    );
};

/**
 * Adds the year-on-year delta to an ad metric cell, as a tooltip.
 *
 * The cell already carries the period-on-period delta inline; a second figure in
 * the same slot would not be readable next to the bar.
 *
 * `evolution` is the backend's own figure (`gads_*_yoy`), not one recomputed
 * here: it already applies the "no evolution against a zero or missing
 * reference" convention the SQL deltas use, and the same figure derived by two
 * formulas drifts the day one of them changes. Absent evolution, absent
 * reference: no tooltip.
 */
const withYearOverYear = (
    cell: React.ReactNode,
    evolution: unknown,
    yearAgo: unknown,
    label: string,
    format: (value: number) => string,
): React.ReactNode => {
    if (typeof evolution !== 'number' || typeof yearAgo !== 'number') return cell;
    const sign = evolution >= 0 ? '+' : '';
    return (
        <CustomTooltip
            title={`${label}: ${format(yearAgo)} (${sign}${evolution.toFixed(0)}%)`}
            placement="top"
        >
            <Box sx={{ width: 1, height: 1 }}>{cell}</Box>
        </CustomTooltip>
    );
};

/**
 * Tooltip body shared by the two competitor columns, Top competitors and New
 * competitors — same content, same behaviour, so the two are one component.
 *
 * A component rather than a helper called from `renderCell`: every line links to
 * the domain's Ads page, and that needs `useNavigate`.
 */
const CompetitorsTooltipContent = ({
    competitors,
    rates,
    topSearchTermsByDomain,
    brandDomain,
    brandRate,
    countryName,
    responsiveConfig,
}: {
    competitors: string[];
    rates: Record<string, number>;
    topSearchTermsByDomain?: Record<string, string[]>;
    brandDomain?: string;
    brandRate?: number | null;
    countryName?: string;
    responsiveConfig: ResponsiveConfig;
}) => {
    const navigate = useNavigate();

    const goToAds = (domain: string) => (event: React.MouseEvent) => {
        event.stopPropagation();
        const countryEntry = countriesJson?.find((c) => c.name === countryName);
        const code = countryEntry?.code?.toUpperCase() || '';
        navigate(
            `/brand/visibility/ads?country=${encodeURIComponent(code)}&domain=${encodeURIComponent(domain)}`
        );
    };

    const formatRate = (rate: number | null) => {
        if (rate === null) return '-';
        return Math.round(rate) === 0 ? '<1%' : `${Math.round(rate)}%`;
    };

    return (
        <Box sx={{ width: 320, boxSizing: 'border-box', backgroundColor: 'rgba(252, 252, 253, 1)', boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.20)' }}>
            <Stack direction="column">
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                        padding: '14px 16px 0 16px'
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{
                            fontWeight: 500,
                            fontSize: responsiveConfig.fontSize.caption
                        }}>
                        {'Competitors'}
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{
                            color: '#637381',
                            textAlign: 'right',
                            fontFamily: 'Public Sans',
                            fontSize: '14px',
                            fontStyle: 'normal',
                            fontWeight: 400,
                            lineHeight: '14px',
                        }}
                    >
                        Paid PR
                    </Typography>
                </Stack>
                <Stack direction="column" sx={{
                    padding: '12px'
                }}>
                    {brandDomain ? (
                        <Stack
                            key={`brand-${brandDomain}`}
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{
                                p: "4px",
                                borderRadius: "6px",
                                backgroundColor: "#d9f3e4",
                            }}
                        >
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                                <Box
                                    component="img"
                                    src={domainFavicon(brandDomain)}
                                    sx={{
                                        width: "18px",
                                        height: "18px",
                                        backgroundColor: '#fff',
                                        borderRadius: '50%',
                                        display: 'block',
                                    }}
                                />
                                <Typography
                                    variant="caption"
                                    noWrap
                                    sx={{
                                        color: '#333C47',
                                        fontFamily: 'Public Sans',
                                        fontSize: '14px',
                                        fontStyle: 'normal',
                                        fontWeight: 400,
                                        lineHeight: '22px',
                                        maxWidth: 180,
                                    }}
                                >
                                    {brandDomain}
                                </Typography>
                            </Stack>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: '#637381',
                                    fontFamily: 'Public Sans',
                                    fontSize: '14px',
                                    fontStyle: 'normal',
                                    fontWeight: 500,
                                    lineHeight: '22px',
                                }}
                            >
                                {brandRate != null ? `${brandRate}%` : '-'}
                            </Typography>
                        </Stack>
                    ) : null}
                    {competitors.map((competitor: string) => {
                        const rate = typeof rates[competitor] === 'number' ? rates[competitor] : null;
                        // Visibility > Local adds the search terms the domain is
                        // most present on. Absent elsewhere: the tooltip is then
                        // unchanged.
                        const topSearchTerms: string[] = Array.isArray(topSearchTermsByDomain?.[competitor])
                            ? (topSearchTermsByDomain as Record<string, string[]>)[competitor]
                            : [];
                        return (
                            <Box key={competitor}>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    padding={"4px"}
                                    // The whole line is the link to the domain's Ads
                                    // page. The tooltip is interactive, so the pointer
                                    // can reach it.
                                    onClick={goToAds(competitor)}
                                    sx={{
                                        cursor: 'pointer',
                                        borderRadius: '6px',
                                        '&:hover': {
                                            backgroundColor: 'rgba(145, 158, 171, 0.12)',
                                            '& .competitor-tooltip-domain': {
                                                color: '#006C9C',
                                                textDecoration: 'underline',
                                            },
                                        },
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                                        <Box
                                            component="img"
                                            src={domainFavicon(competitor)}
                                            sx={{
                                                width: "18px",
                                                height: "18px",
                                                backgroundColor: '#fff',
                                                borderRadius: '50%',
                                                display: 'block',
                                            }}
                                        />
                                        <Typography
                                            className="competitor-tooltip-domain"
                                            variant="caption"
                                            noWrap
                                            sx={{
                                                color: '#333C47',
                                                fontFamily: 'Public Sans',
                                                fontSize: '14px',
                                                fontStyle: 'normal',
                                                fontWeight: 400,
                                                lineHeight: '22px',
                                                maxWidth: 180,
                                            }}
                                        >
                                            {competitor}
                                        </Typography>
                                    </Stack>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: '#637381',
                                            fontFamily: 'Public Sans',
                                            fontSize: '14px',
                                            fontStyle: 'normal',
                                            fontWeight: 500,
                                            lineHeight: '22px',
                                        }}
                                    >
                                        {formatRate(rate)}
                                    </Typography>
                                </Stack>
                                {topSearchTerms.length > 0 && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        aria-label={`top-search-terms-${competitor}`}
                                        sx={{
                                            display: 'block',
                                            px: 1.5,
                                            pb: 0.75,
                                            fontSize: responsiveConfig.fontSize.caption,
                                        }}
                                    >
                                        {topSearchTerms.join(' · ')}
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                </Stack>
            </Stack>
        </Box>
    );
};

export const createDataGridColumns = ({
    responsiveConfig,
    theme,
    t,
    workspace,
    sortModel,
    toggleTrafficShareSort,
    compare = false,
    PresenceRateChart,
    columnType = 'country',
    cellMode,
    selectedCountry,
    adMetricsMax,
    trafficMetricsMax,
}: DataGridConfigProps): GridColDef[] => {
    // Visibility > Search terms traffic columns, built only for the page that opts in
    // through `trafficMetricsMax`.
    const buildSearchTermsTrafficColumns = (
        pageMax: NonNullable<DataGridConfigProps['trafficMetricsMax']>,
    ): GridColDef[] => {
        // Same formats as the Traffic page grid (`useGetFormatter` of generic-data-grid):
        // `currency:1` for Ad cost, compact above 1000, and `float:0` for the traffic.
        const locale = NumberFormatter.getCurrentLocale();
        const currencyOptions: Intl.NumberFormatOptions = {
            style: 'currency',
            currency: workspace?.currency || 'USD',
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
        };
        const standardCurrency = new Intl.NumberFormat(locale, currencyOptions);
        const compactCurrency = new Intl.NumberFormat(locale, { ...currencyOptions, notation: 'compact' });
        const count = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
        const formatCost = (value: number) => (value > 1000 ? compactCurrency : standardCurrency).format(value);
        const formatCount = (value: number) => count.format(value);
        const valueOf = (row: any, field: SearchTermsTrafficField) =>
            typeof row?.[field] === 'number' ? row[field] : 0;
        // Compare mode: the value of the compare period, which the Traffic page shows
        // as an inline evolution in %. Null when that period has no row for the term.
        const compareOf = (row: any, field: SearchTermsTrafficField) => {
            const previous = row?.[`${field}Compare`];
            return compare && typeof previous === 'number' ? previous : null;
        };

        // Same cells as the Traffic page grid too: a magnitude bar for Ad cost, the
        // composition bar of the organic / paid split for Brand traffic, and the value
        // alone for Paid / Organic traffic.
        const adCostMax = pageMax.adCost ?? 0;
        // Sized once for the column, on its widest value. Not on `formatCost(max)`
        // alone: the compact notation starts above 1000, so a row at 1000
        // (`€1,000.0`) can be longer than a compacted max (`€2.5K`).
        const adCostWidth = valueSlotWidth(
            [adCostMax, Math.min(adCostMax, 1000)].map(formatCost).reduce((a, b) => (b.length > a.length ? b : a)),
        );
        const renderAdCost = (row: any) => {
            const value = valueOf(row, 'adCost');
            const previous = compareOf(row, 'adCost');
            return renderMetricCell({
                displayValue: formatCost(value),
                barValue: value,
                max: adCostMax,
                barColor: SEARCH_TERMS_AD_COST_COLOR,
                valueWidth: adCostWidth,
                showCompare: previous !== null,
                current: value,
                compare: previous,
                // Inverted like the catalog's `ad_cost` row: a higher cost reads red.
                invertedColor: true,
                evolutionMode: true,
            });
        };

        // Two segments where the Traffic page draws up to three: `paid_traffic` is not
        // split between search and shopping, so it takes the Paid Search color.
        const renderBrandTraffic = (row: any) => {
            const value = valueOf(row, 'brandTraffic');
            const previous = compareOf(row, 'brandTraffic');
            return renderBrandTrafficCell({
                displayValue: formatCount(value),
                tooltipTitle: t('dashboard.global.visibility.brandTraffic'),
                segments: [
                    {
                        label: t('dashboard.global.visibility.organicTraffic'),
                        value: valueOf(row, 'organicTraffic'),
                        color: BRAND_TRAFFIC_3WAY_COLORS.organic,
                    },
                    {
                        label: t('dashboard.global.visibility.paidTraffic'),
                        value: valueOf(row, 'paidTraffic'),
                        color: BRAND_TRAFFIC_3WAY_COLORS.paidSearch,
                    },
                ],
                showCompare: previous !== null,
                current: value,
                compare: previous,
                evolutionMode: true,
            });
        };

        const renderValue = (field: SearchTermsTrafficField) => (row: any) => {
            const value = valueOf(row, field);
            const previous = compareOf(row, field);
            return (
                <Stack direction="row" alignItems="center" sx={{ gap: '8px', width: 1, height: 1 }}>
                    <Typography
                        sx={{
                            fontSize: '14px',
                            fontWeight: 500,
                            lineHeight: '22px',
                            color: '#333C47',
                            whiteSpace: 'nowrap',
                            fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        {formatCount(value)}
                    </Typography>
                    {previous !== null && (
                        <>
                            <Box sx={{ flex: 1 }} />
                            <Typography
                                sx={{
                                    flexShrink: 0,
                                    textAlign: 'right',
                                    fontSize: '12px',
                                    fontWeight: 400,
                                    lineHeight: 1.2,
                                    whiteSpace: 'nowrap',
                                    fontVariantNumeric: 'tabular-nums',
                                    color: compareColorPct(value, previous),
                                }}
                            >
                                {formatComparePct(value, previous)}
                            </Typography>
                        </>
                    )}
                </Stack>
            );
        };

        const column = (
            field: SearchTermsTrafficField,
            headerKey: string,
            definitionKey: string,
            renderValueCell: (row: any) => React.ReactNode,
            // The Traffic page's floors: 150 for a value alone, wider with a bar.
            minWidth = 180,
        ): GridColDef => ({
            field,
            headerName: t(headerKey),
            minWidth,
            flex: 1,
            sortable: true,
            filterable: true,
            // Same two operators as the other numeric columns of the grid: `type:
            // 'number'` is deliberately left off, it would right-align the header
            // away from the bar it labels.
            filterOperators: numericFilterOperators,
            renderHeader: (params: any) => (
                <CustomTooltip title={t(definitionKey) ?? ''} placement="top">
                    <span style={{ fontSize: responsiveConfig.fontSize.header }}>
                        {params.colDef.headerName}
                    </span>
                </CustomTooltip>
            ),
            renderCell: (params: any) => renderValueCell(params.row),
        });

        return [
            column('adCost', 'dashboard.global.visibility.adCost', 'definitions.ad_cost', renderAdCost),
            column('brandTraffic', 'dashboard.global.visibility.brandTraffic', 'definitions.brand_traffic', renderBrandTraffic),
            column('paidTraffic', 'dashboard.global.visibility.paidTraffic', 'definitions.paid_brand_traffic', renderValue('paidTraffic'), 150),
            column('organicTraffic', 'dashboard.global.visibility.organicTraffic', 'definitions.organic_brand_traffic', renderValue('organicTraffic'), 150),
        ];
    };

    // Visibility > Local: the row is a location. No traffic-share icon in the
    // header — the local marts do not carry it.
    const locationColumn: GridColDef = {
        field: 'location',
        headerName: t('dashboard.global.visibility.location'),
        width: responsiveConfig.searchTermWidth,
        resizable: true,
        disableColumnMenu: true,
        sortable: true,
        valueGetter: (_value: any, row: any) => row?.location ?? '',
        renderHeader: (params) => (
            <span style={{ fontSize: responsiveConfig.fontSize.header }}>{params.colDef.headerName}</span>
        ),
        renderCell: (params) => (
            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, width: '100%' }}>
                <CustomTooltip title={params.row?.location ?? ''} placement="top">
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: responsiveConfig.fontSize.body,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {params.row?.location}
                    </Typography>
                </CustomTooltip>
                {params.row?.locationType && (
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                        {params.row.locationType}
                    </Typography>
                )}
            </Box>
        ),
    };

    // Choisir la première colonne selon le type
    const firstColumn = columnType === 'location' ? locationColumn : columnType === 'searchTerm' ? {
        field: 'searchTerm',
        headerName: t('dashboard.global.searchTerm'),
        width: responsiveConfig.searchTermWidth,
        resizable: true,
        disableColumnMenu: true,
        sortable: false,
        hideSortIcons: true,
        headerClassName: 'searchTermHeader',
        valueGetter: (_value: any, row: any) => row?.searchTerm ?? '',
        sortComparator: (v1: any, v2: any, params1: any, params2: any) => {
            const r1 = params1.api.getRow(params1.id);
            const r2 = params2.api.getRow(params2.id);
            return (r1?.trafficShare ?? 0) - (r2?.trafficShare ?? 0);
        },
        renderHeader: (params: any) => (
            <Box sx={{ display: 'flex', flex: '1 1 auto', alignItems: 'center', justifyContent: 'space-between', width: '100%', minWidth: responsiveConfig.minWidth }}>
                <span style={{ fontSize: responsiveConfig.fontSize.header }}>{params.colDef.headerName}</span>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: responsiveConfig.spacing.cell, ml: 'auto' }}>
                    <CustomTooltip
                        title={t('dashboard.global.visibility.searchShare') ?? ''}
                        placement="top"
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                }}
                                onClick={(e: any) => {
                                    e.stopPropagation();
                                    toggleTrafficShareSort();
                                }}
                                aria-label="toggle-sort-traffic-share"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                >
                                    <path d="M14.1403 10.5933C13.7162 11.5963 13.0528 12.4801 12.2082 13.1675C11.3636 13.8549 10.3635 14.325 9.29524 14.5365C8.22701 14.7481 7.12323 14.6948 6.08038 14.3812C5.03753 14.0676 4.08736 13.5034 3.31296 12.7378C2.53855 11.9722 1.96349 11.0285 1.63804 9.98931C1.31258 8.9501 1.24665 7.847 1.44601 6.77643C1.64537 5.70586 2.10394 4.70042 2.78164 3.84803C3.45934 2.99563 4.33553 2.32222 5.33361 1.88667M14.1595 5.44878C14.4264 6.09309 14.5901 6.77425 14.6456 7.46723C14.6593 7.63843 14.6662 7.72403 14.6322 7.80114C14.6038 7.86555 14.5476 7.9265 14.4856 7.95996C14.4115 8.00001 14.3189 8.00001 14.1336 8.00001H8.53361C8.34693 8.00001 8.25359 8.00001 8.18228 7.96367C8.11956 7.93172 8.06857 7.88072 8.03661 7.818C8.00028 7.7467 8.00028 7.65336 8.00028 7.46667V1.86667C8.00028 1.68143 8.00028 1.58881 8.04033 1.51466C8.07378 1.45272 8.13473 1.39646 8.19914 1.36807C8.27625 1.33407 8.36185 1.34094 8.53305 1.35466C9.22604 1.41022 9.90719 1.57393 10.5515 1.84081C11.3603 2.17584 12.0953 2.6669 12.7143 3.28596C13.3334 3.90502 13.8244 4.63995 14.1595 5.44878Z" stroke="#5E6D7B" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>

                                <IconButton
                                    size="small"
                                    sx={{
                                        p: 0,
                                        minWidth: 0,
                                        width: 'auto',
                                        height: 'auto',
                                    }}
                                >
                                    {sortModel[0]?.field === 'searchTerm'
                                        ? sortModel[0]?.sort === 'desc'
                                            ? (
                                                <Iconify
                                                    icon="iconamoon:arrow-down-2-light"
                                                    width={responsiveConfig.iconSize.arrow}
                                                />
                                            )
                                            : (
                                                <Iconify
                                                    icon="iconamoon:arrow-up-2-light"
                                                    width={responsiveConfig.iconSize.arrow}
                                                />
                                            )
                                        : null}
                                </IconButton>
                            </Box>
                        </Box>
                    </CustomTooltip>
                </Box>
            </Box>
        ),
        renderCell: (params: any) => (
            <SearchTermCell
                row={params.row}
                responsiveConfig={responsiveConfig}
                cellMode={cellMode}
                selectedCountry={selectedCountry}
                t={t}
            />
        ),
    } : {
        field: 'country',
        headerName: t('dashboard.global.country'),
        width: responsiveConfig.countryWidth,
        disableColumnMenu: true,
        sortable: false,
        valueGetter: (_value: any, row: any) => row?.country ?? '',
        sortComparator: (v1: any, v2: any, params1: any, params2: any) => {
            const r1 = params1.api.getRow(params1.id);
            const r2 = params2.api.getRow(params2.id);
            return (r1?.trafficShare ?? 0) - (r2?.trafficShare ?? 0);
        },
        headerClassName: 'countryColumnHeader',
        renderHeader: (params: any) => (
            <Box
                sx={{
                    display: 'flex',
                    flex: 1,
                    alignItems: 'center',
                    width: '100%',
                    minWidth: 0,
                }}
            >
                <Typography
                    component="span"
                    sx={{
                        fontSize: responsiveConfig.fontSize.header,
                        whiteSpace: 'nowrap',
                    }}
                >
                    {params.colDef.headerName}
                </Typography>

                <Box
                    sx={{
                        ml: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <CustomTooltip
                        title={t('dashboard.global.visibility.searchShare') ?? ''}
                        placement="top"
                    >
                        <Box
                            onClick={(e: any) => {
                                e.stopPropagation();
                                toggleTrafficShareSort();
                            }}
                            aria-label="toggle-sort-traffic-share"
                            sx={{
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                flexShrink: 0,
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M14.1403 10.5933C13.7162 11.5963 13.0528 12.4801 12.2082 13.1675C11.3636 13.8549 10.3635 14.325 9.29524 14.5365C8.22701 14.7481 7.12323 14.6948 6.08038 14.3812C5.03753 14.0676 4.08736 13.5034 3.31296 12.7378C2.53855 11.9722 1.96349 11.0285 1.63804 9.98931C1.31258 8.9501 1.24665 7.847 1.44601 6.77643C1.64537 5.70586 2.10394 4.70042 2.78164 3.84803C3.45934 2.99563 4.33553 2.32222 5.33361 1.88667M14.1595 5.44878C14.4264 6.09309 14.5901 6.77425 14.6456 7.46723C14.6593 7.63843 14.6662 7.72403 14.6322 7.80114C14.6038 7.86555 14.5476 7.9265 14.4856 7.95996C14.4115 8.00001 14.3189 8.00001 14.1336 8.00001H8.53361C8.34693 8.00001 8.25359 8.00001 8.18228 7.96367C8.11956 7.93172 8.06857 7.88072 8.03661 7.818C8.00028 7.7467 8.00028 7.65336 8.00028 7.46667V1.86667C8.00028 1.68143 8.00028 1.58881 8.04033 1.51466C8.07378 1.45272 8.13473 1.39646 8.19914 1.36807C8.27625 1.33407 8.36185 1.34094 8.53305 1.35466C9.22604 1.41022 9.90719 1.57393 10.5515 1.84081C11.3603 2.17584 12.0953 2.6669 12.7143 3.28596C13.3334 3.90502 13.8244 4.63995 14.1595 5.44878Z" stroke="#5E6D7B" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>

                            <IconButton
                                size="small"
                                sx={{
                                    p: 0,
                                    ml: '4px',
                                    minWidth: 0,
                                    width: 'auto',
                                    height: 'auto',
                                }}
                            >
                                {sortModel[0]?.field === 'country'
                                    ? (
                                        sortModel[0]?.sort === 'desc'
                                            ? (
                                                <Iconify
                                                    icon="iconamoon:arrow-down-2-light"
                                                    width={responsiveConfig.iconSize.arrow}
                                                />
                                            ) : (
                                                <Iconify
                                                    icon="iconamoon:arrow-up-2-light"
                                                    width={responsiveConfig.iconSize.arrow}
                                                />
                                            )
                                    )
                                    : null}
                            </IconButton>
                        </Box>
                    </CustomTooltip>
                </Box>
            </Box>
        ),
        renderCell: (params: any) => {
            return (
                <CountryCell row={params.row} responsiveConfig={responsiveConfig} t={t} />
            );
        },
    };

    const coverageColumn: GridColDef = {
        field: 'competitionEvolution',
        headerName: t('dashboard.visibility.coverage'),
        width: responsiveConfig.competitionEvolutionWidth,
        sortable: true,
        align: 'left',
        headerAlign: 'left',
        cellClassName: 'leftAlignCell',
        valueGetter: (_value: any, row: any) => {
            const raw = (row?.levelRaw as string) ?? '';
            return raw === 'high' ? 3 : raw === 'active' ? 2 : raw === 'low' ? 1 : 0;
        },
        sortComparator: (v1, v2) => {
            const order = { high: 3, active: 2, low: 1 };
            const a = typeof v1 === 'string' ? order[v1 as keyof typeof order] || 0 : v1;
            const b = typeof v2 === 'string' ? order[v2 as keyof typeof order] || 0 : v2;
            return a - b;
        },
        renderHeader: (params) => (
            <CustomTooltip title={t('dashboard.global.visibility.tooltips.signalCoverage') ?? ''} placement="top">
                <span style={{ fontSize: responsiveConfig.fontSize.header }}>{params.colDef.headerName}</span>
            </CustomTooltip>
        ),
        renderCell: (params) => {
            if (params.row?.status === 'not scrapped') return null;
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', width: '100%', height: '100%', alignSelf: 'stretch', minWidth: 0, overflow: 'visible' }}>
                    <CompetitionEvolutionCell
                        level={params.row.levelRaw}
                        brandDomain={params.row.brandDomain}
                        country={params.row.country}
                        competitorDomain={params.row.firstCompetitorDomain}
                        brandPresenceRate={params.row.paidPresenceRate}
                        competitorPresenceRate={params.row.firstCompetitorPaidPresenceRate}
                        isDemoBrand={params.row.isDemoBrand}
                    />
                </Box>
            );
        },
    };

    return [
        firstColumn,
        ...(cellMode === 'searchTermAnalysis' ? [] : [coverageColumn]),
        {
            field: 'topCompetitors',
            headerName: t('dashboard.visibility.topDomains'),
            flex: 1,
            minWidth: columnType === 'location'
                ? LOCAL_COMPETITORS_MIN_WIDTH
                : responsiveConfig.topCompetitorsMinWidth,
            sortable: false,
            cellClassName: 'topCompetitorsCell',
            renderHeader: (params) => (
                <CustomTooltip
                    title={t('dashboard.global.kpiDefinitions.topDomains') ?? ''}
                    placement="top"
                >
                    <span
                        style={{
                            fontSize: responsiveConfig.fontSize.header,
                        }}
                    >
                        {params.colDef.headerName}
                    </span>
                </CustomTooltip>
            ),
            renderCell: (params) => {
                if (params.row?.status === 'not scrapped') return null;
                const competitors: string[] = Array.isArray(params.value) ? params.value : [];
                const rates: Record<string, number> = params.row.topCompetitorsPaidRates || {};
                const filteredCompetitors: string[] = competitors.slice(0, 5);

                const first = filteredCompetitors[0];
                if (!first) return null;
                const brandDomain: string | undefined = params.row?.brandDomain;
                const brandRate: number | null = typeof params.row?.paidPresenceRate === 'number' ? Math.round(params.row.paidPresenceRate) : null;
                const cellCountryName = params.row?.country || selectedCountry;
                const tooltipContent = (
                    <CompetitorsTooltipContent
                        competitors={filteredCompetitors}
                        rates={rates}
                        topSearchTermsByDomain={params.row?.topCompetitorsTopSearchTerms}
                        brandDomain={brandDomain}
                        brandRate={brandRate}
                        countryName={cellCountryName}
                        responsiveConfig={responsiveConfig}
                    />
                );

                return (
                    <CustomTooltip
                        title={tooltipContent}
                        // Anchored to the bottom of the cell: from the top, a 320px
                        // card over the first rows of the grid ran off the top of
                        // the window.
                        placement="bottom"
                        componentsProps={{
                            tooltip: {
                                sx: {
                                    width: 320,
                                    maxWidth: 'none',
                                    p: 0,
                                    overflow: 'hidden',
                                },
                            },
                        }}
                    >
                        <Box component="span" sx={{ display: 'inline-flex', width: '100%' }}>
                            <TopCompetitorsCell
                                competitors={filteredCompetitors}
                                rates={rates}
                                responsiveConfig={responsiveConfig}
                                countryName={cellCountryName}
                                t={t}
                            />
                        </Box>
                    </CustomTooltip>
                );
            },
        },
        // Top-5 entrants over the period, right after the top 5 itself as in the
        // mock-up. Specific to Visibility > Local: it is the only page whose
        // toolbar always forces a comparison window, hence the only one whose
        // payload carries `new_competitors`. Elsewhere there is no reference
        // period, and a column of dashes would be noise. The `compare` variant
        // below serves the pages that opt into comparison mode instead.
        ...(columnType !== 'location' ? [] : [{
            field: 'newCompetitors',
            headerName: t('dashboard.global.visibility.newCompetitors'),
            flex: 1,
            minWidth: LOCAL_COMPETITORS_MIN_WIDTH,
            sortable: false,
            cellClassName: 'topCompetitorsCell',
            renderHeader: (params: any) => (
                <CustomTooltip
                    title={t('dashboard.global.visibility.newCompetitorsTooltip') ?? ''}
                    placement="top"
                >
                    <span
                        style={{
                            fontSize: responsiveConfig.fontSize.header,
                        }}
                    >
                        {params.colDef.headerName}
                    </span>
                </CustomTooltip>
            ),
            renderCell: (params: any) => {
                const newCompetitors: string[] = Array.isArray(params.value) ? params.value : [];
                // A dash covers both cases: no entrant, and a location with no
                // reference ranking over the comparison window (the SQL then
                // returns NULL rather than an array). Telling them apart here
                // would need data the mock-up does not plan to display.
                if (newCompetitors.length === 0) {
                    return (
                        <Typography
                            variant="body2"
                            aria-label="new-competitors-empty"
                            sx={{ fontSize: responsiveConfig.fontSize.body, color: 'text.disabled' }}
                        >
                            -
                        </Typography>
                    );
                }
                const cellCountryName = params.row?.country || selectedCountry;
                // Same tooltip as the Top competitors column, over the entrants.
                const tooltipContent = (
                    <CompetitorsTooltipContent
                        competitors={newCompetitors}
                        rates={params.row?.topCompetitorsPaidRates || {}}
                        topSearchTermsByDomain={params.row?.topCompetitorsTopSearchTerms}
                        brandDomain={params.row?.brandDomain}
                        brandRate={
                            typeof params.row?.paidPresenceRate === 'number'
                                ? Math.round(params.row.paidPresenceRate)
                                : null
                        }
                        countryName={cellCountryName}
                        responsiveConfig={responsiveConfig}
                    />
                );
                return (
                    <CustomTooltip
                        title={tooltipContent}
                        placement="bottom"
                        componentsProps={{
                            tooltip: {
                                sx: {
                                    width: 320,
                                    maxWidth: 'none',
                                    p: 0,
                                    overflow: 'hidden',
                                },
                            },
                        }}
                    >
                        <Box component="span" sx={{ display: 'inline-flex', width: '100%' }}>
                            <TopCompetitorsCell
                                competitors={newCompetitors}
                                // The top-5 chips are tinted by paid presence rate;
                                // these carry none — the column answers "who arrived",
                                // not "at what rate".
                                rates={params.row?.topCompetitorsPaidRates || {}}
                                responsiveConfig={responsiveConfig}
                                countryName={cellCountryName}
                                t={t}
                            />
                        </Box>
                    </CustomTooltip>
                );
            },
        }]),
        ...(compare && columnType !== 'location' ? [
            {
                field: 'newCompetitors',
                headerName: 'New competitors over the period',
                flex: 1,
                minWidth: responsiveConfig.topCompetitorsMinWidth,
                sortable: false,
                cellClassName: 'topCompetitorsCell',

                valueGetter: (_value: any, row: any) =>
                    Array.isArray(row?.newCompetitors)
                        ? row.newCompetitors.length
                        : 0,

                renderHeader: (params: any) => (
                    <CustomTooltip
                        title="Competitors present during the selected period but absent from the previous period."
                        placement="top"
                    >
                        <span
                            style={{
                                fontSize: responsiveConfig.fontSize.header,
                            }}
                        >
                            {params.colDef.headerName}
                        </span>
                    </CustomTooltip>
                ),

                renderCell: (params: any) => {
                    if (params.row?.status === 'not scrapped') {
                        return null;
                    }

                    const newCompetitors = Array.isArray(
                        params.row?.newCompetitors
                    )
                        ? params.row.newCompetitors
                        : [];

                    if (newCompetitors.length === 0) {
                        return (
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: responsiveConfig.fontSize.body,
                                    color: 'text.secondary',
                                }}
                            >
                                -
                            </Typography>
                        );
                    }

                    const tooltipContent = (
                        <Box sx={{ minWidth: 280 }}>
                            <Stack direction="column" spacing={1.25}>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    sx={{
                                        px: 1.5,
                                        pt: 1,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontWeight: 700,
                                            fontSize:
                                                responsiveConfig.fontSize.caption,
                                        }}
                                    >
                                        New competitors
                                    </Typography>

                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: 'text.secondary',
                                            fontSize:
                                                responsiveConfig.fontSize.caption,
                                        }}
                                    >
                                        Paid PR
                                    </Typography>
                                </Stack>

                                <Stack direction="column" spacing={0.5}>
                                    {newCompetitors.map(
                                        (competitor: {
                                            domainName: string;
                                            paidPresenceRate: number;
                                        }) => {
                                            const rate =
                                                typeof competitor.paidPresenceRate ===
                                                    'number'
                                                    ? competitor.paidPresenceRate
                                                    : null;

                                            return (
                                                <Stack
                                                    key={competitor.domainName}
                                                    direction="row"
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    sx={{
                                                        px: 1.5,
                                                        py: 0.75,
                                                        borderRadius: 1.5,
                                                    }}
                                                >
                                                    <Stack
                                                        direction="row"
                                                        alignItems="center"
                                                        spacing={1}
                                                        sx={{ minWidth: 0 }}
                                                    >
                                                        <Box
                                                            component="img"
                                                            src={domainFavicon(
                                                                competitor.domainName
                                                            )}
                                                            sx={{
                                                                width:
                                                                    responsiveConfig
                                                                        .iconSize
                                                                        .domain,
                                                                height:
                                                                    responsiveConfig
                                                                        .iconSize
                                                                        .domain,
                                                                backgroundColor:
                                                                    '#fff',
                                                                borderRadius: '50%',
                                                                display: 'block',
                                                            }}
                                                        />

                                                        <Typography
                                                            variant="caption"
                                                            noWrap
                                                            sx={{
                                                                fontSize:
                                                                    responsiveConfig
                                                                        .fontSize
                                                                        .caption,
                                                                maxWidth: 180,
                                                                fontWeight: 500,
                                                            }}
                                                        >
                                                            {
                                                                competitor.domainName
                                                            }
                                                        </Typography>
                                                    </Stack>

                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{
                                                            fontSize:
                                                                responsiveConfig
                                                                    .fontSize
                                                                    .caption,
                                                        }}
                                                    >
                                                        {rate !== null
                                                            ? Math.round(rate) === 0
                                                                ? '<1%'
                                                                : `${Math.round(
                                                                    rate
                                                                )}%`
                                                            : '-'}
                                                    </Typography>
                                                </Stack>
                                            );
                                        }
                                    )}
                                </Stack>
                            </Stack>
                        </Box>
                    );

                    return (
                        <CustomTooltip
                            title={tooltipContent}
                            placement="top"
                        >
                            <Box
                                component="span"
                                sx={{
                                    display: 'inline-flex',
                                    width: '100%',
                                    height: '100%',
                                }}
                            >
                                <NewCompetitorsCell
                                    competitors={newCompetitors}
                                    responsiveConfig={responsiveConfig}
                                    countryName={params.row?.country}
                                />
                            </Box>
                        </CustomTooltip>
                    );
                },
            },
        ] : []),
        {
            field: 'blendedPresenceRate',
            headerName: t('dashboard.global.visibility.blendedVisibilityRate'),
            // Local sizes this column to the mock-up and puts its title back on the
            // left: `type: 'number'` right-aligns the header, which slides the title
            // under the 3-dot menu. Elsewhere the column is untouched — the same fix
            // probably applies, but those pages have not been reviewed.
            ...(columnType === 'location'
                ? {
                    width: LOCAL_VISIBILITY_RATE_WIDTH,
                    minWidth: LOCAL_VISIBILITY_RATE_WIDTH,
                    headerAlign: 'left' as const,
                    align: 'left' as const,
                }
                : { flex: 1, minWidth: responsiveConfig.blendedPresenceMinWidth }),
            type: 'number',
            filterOperators: numericFilterOperators,
            renderHeader: (params) => (
                <CustomTooltip
                    title={
                        t('dashboard.global.kpiDefinitions.blendedPresenceRate') ?? ''
                    }
                    placement="top"
                >
                    <span
                        style={{
                            fontSize: responsiveConfig.fontSize.header,
                        }}
                    >
                        {params.colDef.headerName}
                    </span>
                </CustomTooltip>
            ),
            renderCell: (params) => {
                if (params.row?.status === 'not scrapped') return null;
                const filterStore = useSelector((state: any) => state.filters);

                return (
                    <VisibilityRateCell
                        row={params.row}
                        responsiveConfig={responsiveConfig}
                        PresenceRateChart={PresenceRateChart}
                        compare={filterStore?.compare}
                        selectedCountry={selectedCountry}
                        columnType={columnType}
                        t={t}
                    />
                );
            },
        },
        ...(!['/brand/visibility/search-terms', '/brand/visibility/local'].includes(location?.pathname)
            ? []
            : [{

                field: 'ai_overview_coverage',
                headerName: t('dashboard.global.visibility.aiOverviewCoverage'),
                // Local: aligned on its Ad cost / Ad clicks neighbours. The content
                // fits: value (40) + bar (30 min) + comparison delta (62) and their
                // two gaps make ~148, cell padding included. Search terms keeps its
                // original 300 (ClickUp 12439gnrrfw).
                minWidth: window.location.pathname === '/brand/visibility/local' ? 180 : 300,
                // type: 'number',
                filterOperators: numericFilterOperators,
                sortable: true,
                filterable: true,
                renderHeader: (params: any) => (
                    <CustomTooltip
                        title={
                            t(
                                'dashboard.global.visibility.tooltips.aiOverviewCoverage'
                            ) ?? ''
                        }
                        placement="top"
                    >
                        <span
                            style={{
                                fontSize: responsiveConfig.fontSize.header,
                            }}
                        >
                            {params.colDef.headerName}
                        </span>
                    </CustomTooltip>
                ),

                renderCell: (params: any) => {
                    const value =
                        typeof params.row?.ai_overview_coverage === 'number'
                            ? params.row.ai_overview_coverage
                            : 0;

                    const compareValue =
                        typeof params.row?.aiOverviewCoverageCompare === 'number'
                            ? params.row.aiOverviewCoverageCompare
                            : typeof params.row?.ai_overview_coverage_compare === 'number'
                                ? params.row.ai_overview_coverage_compare
                                : null;

                    const differencePp =
                        compare && compareValue !== null
                            ? Math.round(value - compareValue)
                            : null;

                    return (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                width: '100%',
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    minWidth: 40,
                                    fontSize: responsiveConfig.fontSize.body,
                                    fontWeight: 400,
                                    color: '#333C47',
                                }}
                            >
                                {/* A coverage that exists but rounds to zero reads as
                                    an empty column: akena sits at 0.4%. Same `<1%`
                                    convention as the competitor rates of this grid. */}
                                {value > 0 && Math.round(value) === 0 ? '<1%' : `${Math.round(value)}%`}
                            </Typography>

                            {/* Always rendered, including at 0%: dropping the
                                element shifted the rest of the row and broke the
                                alignment with the other rows. At 0% it carries
                                neither track nor fill. */}
                            <Box
                                aria-label="ai-coverage-bar"
                                sx={{
                                    height: 6,
                                    borderRadius: 999,
                                    flex: 1,
                                    minWidth: 30,
                                    bgcolor: value > 0 ? 'rgba(145, 158, 171, 0.16)' : 'transparent',
                                    overflow: 'hidden',
                                }}
                            >
                                {value > 0 && (
                                    <Box
                                        sx={{
                                            height: '100%',
                                            width: `${Math.min(100, Math.max(0, value))}%`,
                                            borderRadius: 999,
                                            bgcolor: '#B76EFF',
                                        }}
                                    />
                                )}
                            </Box>

                            {differencePp !== null && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        minWidth: 62,
                                        textAlign: 'right',
                                        whiteSpace: 'nowrap',
                                        fontSize: responsiveConfig.fontSize.body,
                                        fontWeight: 500,
                                        color:
                                            differencePp > 0
                                                ? '#00A76F'
                                                : differencePp < 0
                                                    ? '#FF3030'
                                                    : 'text.secondary',
                                    }}
                                >
                                    {differencePp > 0 ? '+' : ''}
                                    {differencePp}pp
                                </Typography>
                            )}

                            {typeof params.row?.aiOverviewCoveragePrevious === 'number' && (
                                <Typography
                                    sx={{
                                        width: EVOLUTION_SLOT_WIDTH,
                                        flexShrink: 0,
                                        textAlign: 'right',
                                        fontSize: '12px',
                                        lineHeight: 1.2,
                                        whiteSpace: 'nowrap',
                                        fontVariantNumeric: 'tabular-nums',
                                        color: compareColorPct(value, params.row.aiOverviewCoveragePrevious),
                                    }}
                                >
                                    {formatCoverageDelta(value, params.row.aiOverviewCoveragePrevious)}
                                </Typography>
                            )}
                        </Box>
                    );
                },
            }]
        ),
        // Visibility > Search terms: the Traffic page metrics of the search term
        // (ClickUp 12439gnt4nf). Opt-in rather than a pathname guard, which already
        // leaked columns across pages (12439gnrrfw). Not on Local: `traffic` has no
        // location dimension, its figures cannot be split or added up by location.
        ...(trafficMetricsMax ? buildSearchTermsTrafficColumns(trafficMetricsMax) : []),
        // Ad cost / clicks come after AI Overview coverage, which the mock-up places
        // between the visibility rate and Ad cost.
        //
        // Visibility > Local only: Search Terms has its own, from `traffic` (above).
        ...(location?.pathname !== '/brand/visibility/local'
            ? []
            : [
                {
                    field: 'adCost',
                    headerName: t('dashboard.global.visibility.adCost'),
                    // Wide enough for the value slot plus a readable bar.
                    minWidth: 180,
                    flex: 1,
                    sortable: true,
                    filterable: true,
                    // Same two operators as the other numeric columns of the grid:
                    // `type: 'number'` is deliberately left off, it would right-align
                    // the header away from the bar it labels.
                    filterOperators: numericFilterOperators,
                    renderHeader: (params: any) => (
                        <CustomTooltip
                            title={t('dashboard.global.visibility.adCostTooltipLocation') ?? ''}
                            placement="top"
                        >
                            <span
                                style={{
                                    fontSize: responsiveConfig.fontSize.header,
                                }}
                            >
                                {params.colDef.headerName}
                            </span>
                        </CustomTooltip>
                    ),
                    renderCell: (params: any) => {
                        // `null` on Local when the location has no Google Ads
                        // match: the LEFT JOIN keeps the row, and the cell shows a
                        // dash rather than a spend of zero the client never had.
                        const raw = params.row?.adCost;
                        const hasValue = typeof raw === 'number';
                        const value = hasValue ? raw : 0;
                        const max = adMetricsMax?.adCost ?? 0;
                        const fmt = (amount: number) => formatCurrency(
                            amount,
                            workspace?.currency || 'USD',
                            NumberFormatter.getCurrentLocale(),
                            true,
                        );
                        const compareValue = params.row?.adCostPrevious;
                        const cell = renderMetricCell({
                            displayValue: hasValue ? fmt(value) : '-',
                            barValue: value,
                            max,
                            barColor: AD_METRICS_BAR_COLOR,
                            valueWidth: valueSlotWidth(fmt(max)),
                            // Signed `+35%`, as in the mock-up. Not inverted: it
                            // shows a cost increase in green.
                            showCompare: hasValue && typeof compareValue === 'number',
                            current: value,
                            compare: compareValue,
                            evolutionMode: true,
                        });
                        return withYearOverYear(
                            cell,
                            hasValue ? params.row?.adCostYoy : null,
                            params.row?.adCostYearAgo,
                            t('dashboard.global.visibility.adCostYearAgo'),
                            fmt,
                        );
                    },
                },
                {
                    field: 'adClicks',
                    headerName: t('dashboard.global.visibility.adClicks'),
                    minWidth: 180,
                    flex: 1,
                    sortable: true,
                    filterable: true,
                    // Same two operators as the other numeric columns of the grid:
                    // `type: 'number'` is deliberately left off, it would right-align
                    // the header away from the bar it labels.
                    filterOperators: numericFilterOperators,
                    renderHeader: (params: any) => (
                        <CustomTooltip
                            title={t('dashboard.global.visibility.adClicksTooltipLocation') ?? ''}
                            placement="top"
                        >
                            <span
                                style={{
                                    fontSize: responsiveConfig.fontSize.header,
                                }}
                            >
                                {params.colDef.headerName}
                            </span>
                        </CustomTooltip>
                    ),
                    renderCell: (params: any) => {
                        const raw = params.row?.adClicks;
                        const hasValue = typeof raw === 'number';
                        const value = hasValue ? raw : 0;
                        const max = adMetricsMax?.adClicks ?? 0;
                        const compareValue = params.row?.adClicksPrevious;
                        const cell = renderMetricCell({
                            displayValue: hasValue ? fShortenNumber(value) : '-',
                            barValue: value,
                            max,
                            barColor: AD_METRICS_BAR_COLOR,
                            valueWidth: valueSlotWidth(fShortenNumber(max)),
                            showCompare: hasValue && typeof compareValue === 'number',
                            current: value,
                            compare: compareValue,
                            evolutionMode: true,
                        });
                        return withYearOverYear(
                            cell,
                            hasValue ? params.row?.adClicksYoy : null,
                            params.row?.adClicksYearAgo,
                            t('dashboard.global.visibility.adClicksYearAgo'),
                            (count: number) => fShortenNumber(count),
                        );
                    },
                },
            ]
        ),
    ];
};
